import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Period } from 'lumen/models';
import type { RateGroup } from 'lumen/models/pa-subscription-meta-info';
import {
  formatNamesLabel,
  groupByMinLos,
} from 'lumen/utils/pricing-assistant/rate-group-restriction-utils';
import { formatMinLosValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './pricing-assistant-table-tooltip.module.css';

interface RateGroupRow {
  formattedValue: string;
  label: string;
}

interface HotelPricingAssistantTableTooltipsTooltipRateGroupsMonthSignature {
  Args: {
    // The hotel level cell's own MinLOS, listed above the rate groups
    hotelMinLos?: number | null;
    period: Period;
    rateGroups: RateGroup[];
  };
}

export default class HotelPricingAssistantTableTooltipsTooltipRateGroupsMonth extends Component<HotelPricingAssistantTableTooltipsTooltipRateGroupsMonthSignature> {
  @service declare dates: Services['dates'];
  @service declare intl: Services['intl'];

  get formattedDate(): string {
    return this.args.period.fromDate.format(
      this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
    );
  }

  /* The month view shows names and values only, so override is cleared before
     grouping: rate groups with the same value share one row. */
  get rows(): RateGroupRow[] {
    const entries = this.args.rateGroups.map(({ rateGroupId }) => ({
      ...this.args.period.rateGroupMinLos(rateGroupId),
      hasOverride: false,
    }));

    const rateGroupRows = groupByMinLos(entries).map((row) => ({
      formattedValue: formatMinLosValue({ intl: this.intl, value: row.value }),
      label: formatNamesLabel(
        row.items.map(({ rateGroupId }) => this.rateGroupName(rateGroupId)),
        this.intl,
      ),
    }));

    if (this.args.hotelMinLos === undefined) {
      return rateGroupRows;
    }

    return [
      {
        formattedValue: formatMinLosValue({
          intl: this.intl,
          value: this.args.hotelMinLos,
        }),
        label: this.intl.t('general.hotel'),
      },
      ...rateGroupRows,
    ];
  }

  /* Same fallback as the week view: the id rather than a blank label. */
  rateGroupName(rateGroupId: string): string {
    const rateGroup = this.args.rateGroups.find(
      (group) => group.rateGroupId === rateGroupId,
    );

    return rateGroup?.name || rateGroupId;
  }

  <template>
    <EmberTooltip
      @side="bottom"
      @tooltipClass="base-tooltip {{styles.container}}"
    >
      <div
        class="{{styles.header}} p-400"
        data-test-id="tooltip-rate-groups-month-header"
      >
        <h4>{{this.formattedDate}}</h4>
      </div>
      <table
        class="default-table {{styles.table}} {{styles.wrapping-names-table}}"
      >
        <tbody>
          {{#each this.rows as |row|}}
            <tr data-test-id="tooltip-rate-groups-month-row">
              <td class="{{styles.table-row-cell}} text-200--regular">
                <p class="text-200--regular {{styles.wrapping-cell}}">
                  {{row.label}}
                </p>
                <p class="text-200--regular {{styles.fixed-cell}}">
                  {{row.formattedValue}}
                </p>
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>
    </EmberTooltip>
  </template>
}
